/**
 * Mount point the events-scores lane builds inside.
 *
 * Search params are `?leagueId=`, `?date=`, `?week=`, `?sheet=`, `?view=`, `?grouping=`,
 * `?teamId=`, `?eventId=`, `?tab=`, `?lineupTeam=`, `?shareEventId=` and
 * `?alertsEventId=` per `apps/app/src/routeState/sectionSearch.ts`. `eventId` present renders the
 * game detail screen (ISSUE-180), taking priority over every other branch --
 * a score row's link carries both `leagueId` and `eventId` in its merged
 * search, so `eventId` must win. Otherwise `teamId` present renders the team
 * profile (ISSUE-182), above the standings and league branches -- a
 * standings row's link merges `teamId` into a search that still carries
 * `leagueId` and `view=standings`, so `teamId` must outrank both. Otherwise
 * `leagueId` absent renders the `All` feed; present renders the league
 * drill-down. `?view=standings` plus a `leagueId` renders the standings
 * screen instead. There is no Games or Standings toggle. Every piece of
 * state this screen carries is a search param, so a shared link opens the
 * same view. Every standings row and the game header's team columns
 * navigate into the team profile; a score row is one target that opens the
 * game (Scores UX spec v1.0 section 1.8). The league chip row is mounted here,
 * once, above both game panes, so changing ?leagueId= swaps only the pane
 * beneath it and never unmounts the row.
 *
 * `ShareToChatDialog` is mounted once here and opened by the game detail
 * header's `Share` action (`?dialog=share&shareEventId=`). After a send it
 * shows its own confirmation with Done and Open chat, and with no chats it
 * offers Start a chat; both actions go to /messages with replace, and
 * closing it clears shareEventId. A score row's bell (ISSUE-979) opens
 * `GameAlertsDialog` (`?sheet=row-alerts&alertsEventId=`), mounted once here
 * and only while its id is in the URL, so a list never fetches alert state
 * for a game nobody opened.
 */
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { ScoresHeading } from "../features/scoreSearch/ScoresHeading";
import { GameAlertsDialog } from "../features/gameAlerts/GameAlertsDialog";
import { SHARE_DIALOG } from "../features/gameDetail/GameDetailHeader";
import { GameDetailScreen } from "../features/gameDetail/GameDetailScreen";
import { NEW_CHAT_DIALOG } from "../features/messages/NewChatDialog";
import { isDayString } from "../features/scores/calendarDay";
import { CONFERENCE_SHEET } from "../features/scores/ConferenceFilter";
import { LeagueDrillDownPane } from "../features/scores/LeagueDrillDownPane";
import { LeagueMenuDialog } from "../features/scores/LeagueMenuDialog";
import { ROW_ALERTS_SHEET } from "../features/scores/ScoreRowActions";
import { ScoresHomePane } from "../features/scores/ScoresHomePane";
import { ScoresLeagueTabs } from "../features/scores/ScoresLeagueTabs";
import { ShareToChatDialog } from "../features/shareGame/ShareToChatDialog";
import { StandingsScreen } from "../features/standings/StandingsScreen";
import { TeamProfileScreen } from "../features/team/TeamProfileScreen";
import { validateScoresSearch } from "../routeState/sectionSearch";
import { useOverlay } from "../routeState/useOverlay";
import { SectionScreen } from "../shell/SectionScreen";

const ScoresScreen = (): ReactNode => {
  const search = useSearch({ from: "/scores" });
  const navigate = useNavigate({ from: "/scores" });
  const overlay = useOverlay();

  const onSelectLeague = (leagueId: string): void => {
    void navigate({
      search: (prev) => ({
        ...prev,
        leagueId,
        date: undefined,
        week: undefined,
        conference: undefined,
        sheet: undefined,
      }),
    });
  };
  const onSelectAll = (): void => {
    void navigate({
      search: (prev) => ({
        ...prev,
        leagueId: undefined,
        date: undefined,
        week: undefined,
        conference: undefined,
        sheet: undefined,
      }),
    });
  };
  const onOpenMenu = (): void => {
    void navigate({ search: (prev) => ({ ...prev, sheet: "leagues" }) });
  };
  const onCloseSheet = (): void => {
    void navigate({ search: (prev) => ({ ...prev, sheet: undefined }) });
  };
  const onOpenCalendar = (): void => {
    void navigate({ search: (prev) => ({ ...prev, sheet: "calendar" }) });
  };
  const onSelectDay = (day: string): void => {
    void navigate({
      search: (prev) => ({ ...prev, date: day }),
      replace: true,
    });
  };
  // `date` is cleared on purpose. On a week-based league the two keys would
  // otherwise both claim the selection, and `?date=` is only ever read there
  // as the seed for which week an already-shared day link lands on.
  const onSelectWeek = (seasonWeek: number): void => {
    void navigate({
      search: (prev) => ({ ...prev, week: seasonWeek, date: undefined }),
      replace: true,
    });
  };
  const onOpenGame = (eventId: string): void => {
    void navigate({
      search: (prev) => ({
        ...prev,
        eventId,
        tab: undefined,
        lineupTeam: undefined,
      }),
    });
  };
  const date =
    search.date !== undefined && isDayString(search.date)
      ? search.date
      : undefined;

  // The chip row belongs to the two game panes only. Game detail, the team
  // profile and the standings screen each carry their own frame.
  const isGamesView =
    search.eventId === undefined &&
    search.teamId === undefined &&
    !(search.view === "standings" && search.leagueId !== undefined);

  return (
    // A game, a team and standings each draw their own h1 in their top row,
    // so "Scores" heads only the two game panes (spec 7.1 G8, 8, 10).
    // ISSUE-1380: the games panes' heading row carries the search entry.
    <SectionScreen heading={null}>
      {isGamesView ? <ScoresHeading /> : null}
      <LeagueMenuDialog
        open={search.sheet === "leagues"}
        onClose={onCloseSheet}
        onSelectLeague={onSelectLeague}
      />
      {isGamesView ? (
        <ScoresLeagueTabs
          activeLeagueId={search.leagueId ?? null}
          onSelectAll={onSelectAll}
          onSelectLeague={onSelectLeague}
          onOpenMenu={onOpenMenu}
        />
      ) : null}
      {search.eventId !== undefined ? (
        <GameDetailScreen
          eventId={search.eventId}
          tab={search.tab}
          lineupTeam={search.lineupTeam}
        />
      ) : search.teamId !== undefined ? (
        <TeamProfileScreen teamId={search.teamId} tab={search.tab} />
      ) : search.view === "standings" && search.leagueId !== undefined ? (
        <StandingsScreen
          leagueId={search.leagueId}
          grouping={search.grouping}
        />
      ) : search.leagueId === undefined ? (
        <ScoresHomePane
          onSelectLeague={onSelectLeague}
          onOpenMenu={onOpenMenu}
          onOpenGame={onOpenGame}
        />
      ) : (
        <LeagueDrillDownPane
          leagueId={search.leagueId}
          date={date}
          week={search.week}
          isCalendarOpen={search.sheet === "calendar"}
          onSelectDay={onSelectDay}
          onSelectWeek={onSelectWeek}
          onOpenCalendar={onOpenCalendar}
          onCloseCalendar={onCloseSheet}
          onOpenGame={onOpenGame}
          conference={search.conference}
          isConferenceOpen={search.sheet === CONFERENCE_SHEET}
          onOpenConference={() => {
            void navigate({
              search: (prev) => ({ ...prev, sheet: CONFERENCE_SHEET }),
            });
          }}
          onCloseConference={onCloseSheet}
          onSelectConference={(conference) => {
            void navigate({
              search: (prev) => ({ ...prev, conference, sheet: undefined }),
              replace: true,
            });
          }}
        />
      )}
      {search.alertsEventId === undefined ? null : (
        <GameAlertsDialog
          eventId={search.alertsEventId}
          open={overlay.isOpen("sheet", ROW_ALERTS_SHEET)}
          onClose={() => {
            overlay.close({ kind: "sheet", clearParams: ["alertsEventId"] });
          }}
        />
      )}
      {search.shareEventId === undefined ? null : (
        <ShareToChatDialog
          eventId={search.shareEventId}
          open={overlay.isOpen("dialog", SHARE_DIALOG)}
          onClose={() => {
            overlay.close({ kind: "dialog", clearParams: ["shareEventId"] });
          }}
          onOpenChat={(chatId) => {
            void navigate({
              to: "/messages",
              search: { conversationId: chatId },
              replace: true,
            });
          }}
          onStartChat={() => {
            void navigate({
              to: "/messages",
              search: { dialog: NEW_CHAT_DIALOG },
              replace: true,
            });
          }}
        />
      )}
    </SectionScreen>
  );
};

export const Route = createFileRoute("/scores")({
  beforeLoad: requireSignedIn,
  validateSearch: validateScoresSearch,
  component: ScoresScreen,
});
